import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StyledLink from "@tSurya/theme/components/StyledLink";
import { COLORS, ELEMENTS, TYPOGRAPHY, WIDGETS } from "@tSurya/theme/design";
import VideoBanner from "@tSurya/Components/VideoBanner";
import * as cssLinter from "css";
import parse from "html-react-parser";
import uniqueId from "lodash/uniqueId";
import React from "react";
import styled, { css } from "styled-components";
import { isGlobalViewsWebsite } from "@tSurya/Common/Utilities/websiteUtils";

const enum fields {
    preTitle = "preTitle",
    mainTitle = "mainTitle",
    infoDesc = "infoDesc",
    gridImage = "gridImage",
    imageAligment = "imageAligment",
    cssClass = "cssClass",
    contentTitle = "contentTitle",
    imageLink = "imageLink",
    contentDesc = "contentDesc",
    addContactDetails = "addContactDetails",
    addLinks = "addLinks",
    hours = "hours",
    email = "email",
    phone = "phone",
    links = "links",
    brandImage = "brandImage",
    brandDates = "brandDates",
    openInNewWidow = "openInNewWidow",
    buttonBackgroundColor = "buttonBackgroundColor",
    buttonTextColor = "buttonTextColor",
    customCSS = "customCSS",
    backgroundColor = "backgroundColor",
    paddingTopBottom = "paddingTopBottom",
    paddingLeftRight = "paddingLeftRight",
    mobilePaddingTopBottom = "mobilePaddingTopBottom",
    mobilePaddingLeftRight = "mobilePaddingLeftRight",
    marginTop = "marginTop",
    mobileMarginTop = "mobileMarginTop",
    imageAspectRatio = "imageAspectRatio",
    mediaType = "mediaType",
    gridVideo = "gridVideo",
    videoPoster = "videoPoster",
    videoLoop = "videoLoop",
    videoMuted = "videoMuted",
    videoAutoplay = "videoAutoplay",
    videoHideControls = "videoHideControls",
    videoPlaysInline = "videoPlaysInline",
    mobileImagePosition = "mobileImagePosition",
    textAlign = "textAlign",
    linksLayout = "linksLayout",
}

interface LinkInterface {
    label?: string;
    link: LinkFieldValue;
    openInNewWindow?: boolean;
    variant?: string;
    buttonBackgroundColor?: string;
    buttonTextColor?: string;
}

interface LinksModel {
    fields: LinkInterface;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.preTitle]: string;
        [fields.mainTitle]: string;
        [fields.contentTitle]: string;
        [fields.infoDesc]: string;
        [fields.contentDesc]: string;
        [fields.gridImage]: string;
        [fields.imageAligment]: string;
        [fields.customCSS]: string;
        [fields.backgroundColor]: string;
        [fields.paddingTopBottom]: string;
        [fields.paddingLeftRight]: string;
        [fields.mobilePaddingTopBottom]: string;
        [fields.mobilePaddingLeftRight]: string;
        [fields.marginTop]: string;
        [fields.mobileMarginTop]: string;
        [fields.imageAspectRatio]: string;
        [fields.addContactDetails]: boolean;
        [fields.addLinks]: boolean;
        [fields.hours]: string;
        [fields.email]: string;
        [fields.phone]: number;
        [fields.phone]: number;
        [fields.links]: LinksModel[];
        [fields.buttonBackgroundColor]: string;
        [fields.buttonTextColor]: string;
        [fields.brandImage]: string;
        [fields.brandDates]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.openInNewWidow]: boolean;
        [fields.mediaType]: string;
        [fields.gridVideo]: string;
        [fields.videoPoster]: string;
        [fields.videoLoop]: boolean;
        [fields.videoMuted]: boolean;
        [fields.videoAutoplay]: boolean;
        [fields.videoHideControls]: boolean;
        [fields.videoPlaysInline]: boolean;
        [fields.mobileImagePosition]: string;
        [fields.textAlign]: string;
        [fields.linksLayout]: string;
    };
}

type Props = OwnProps;

export interface SurImageGridStyles {
    container?: InjectableCss;
    infoTitleContainer?: InjectableCss;
    contactInfoContainer?: InjectableCss;
    gridContainer?: GridContainerProps;
    preTitle?: TypographyProps;
    mainTitle?: TypographyProps;
    desc?: TypographyProps;
    imageGridItemStyles?: GridItemProps;
    contentGridStyles?: GridItemProps;
    contentTitleStyles?: TypographyProps;
    contentDescStyles?: TypographyProps;
    contactInfo?: TypographyProps;
    cta?: LinkProps;
    linksCta?: LinkProps;
    linksContainer?: InjectableCss;
    fullWidth?: InjectableCss;
    goldenCTA?: LinkProps;
    normalCTA?: LinkProps;
    brandImgStyles?: InjectableCss;
    brandDateStyles?: InjectableCss;
}

const isGv = isGlobalViewsWebsite();

export const imageGridStyles: SurImageGridStyles = {
    container: {
        css: css`
            &.max-width-container {
                max-width: ${(WIDGETS.imageGrid as any).containerMaxWidth || "1360px"} !important;
            }
            &:first-child {
                margin-top: 0;
            }
            margin: ${(WIDGETS.imageGrid as any).containerMargin || "30px 0 0"};
            &:last-child {
                margin-bottom: ${(WIDGETS.imageGrid as any).containerMarginBottom || "50px"};
            }
            > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                justify-content: ${(WIDGETS.imageGrid as any).gridJustifyContent || "center"};
                padding: ${(WIDGETS.imageGrid as any).verticalPadding || "30px 0"};
                margin: 0 !important;
                width: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0;
                        &:last-child {
                            margin-bottom: ${(WIDGETS.imageGrid as any).mobileLastChildMarginBottom || "30px"};
                        }

                        > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                            width: 100% !important;
                            padding: ${(WIDGETS.imageGrid as any).mobilePadding || `15px ${WIDGETS.imageGrid.mobileXPadding}`};
                        }
                    `,
                    css`
                        margin: 0;
                        > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                            width: 100% !important;
                            padding: ${(WIDGETS.imageGrid as any).mobilePadding || `15px ${WIDGETS.imageGrid.mobileXPadding}`};
                        }
                    `,
                    css`
                        margin: 0;
                        > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                            width: 100% !important;
                            margin: 0 auto !important;
                            padding: ${(WIDGETS.imageGrid as any).tabletPadding || `30px ${WIDGETS.imageGrid.mobileXPadding}`};
                        }
                    `,
                    css`
                        margin: 0;
                        > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                            padding: ${(WIDGETS.imageGrid as any).lgPadding ||
                            (WIDGETS.imageGrid as any).verticalPadding ||
                            `30px ${WIDGETS.imageGrid.mobileXPadding}`};
                        }
                    `,
                    null,
                ])}

            @media (min-width: 992px) and (max-width: 1369px) {
                padding-left: 20px;
                padding-right: 20px;
            }
        `,
    },
    infoTitleContainer: {
        css: css`
            margin-bottom: 36px;
        `,
    },
    gridContainer: {
        gap: 0,
        css: css`
            width: 100% !important;
            > div[class^="GridOffset"] {
                gap: ${(WIDGETS.imageGrid as any).desktopGap || "60px"};
                flex-wrap: nowrap;

            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > div[class^="GridOffset"] {
                            gap: 32px;
                            flex-wrap: wrap;
                        }
                        > div[class^="GridOffset"] > div[class^="GridItemStyle"] {
                            width: 100% !important;
                            flex-basis: 100% !important;
                            max-width: 100% !important;
                        }
                    `,
                    css`
                        > div[class^="GridOffset"] {
                            gap: 32px;
                            flex-wrap: wrap;
                        }
                        > div[class^="GridOffset"] > div[class^="GridItemStyle"] {
                            width: 100% !important;
                            flex-basis: 100% !important;
                            max-width: 100% !important;
                        }
                    `,
                    css`
                        > div[class^="GridOffset"] {
                            gap: 32px;
                            flex-wrap: wrap;
                        }
                        > div[class^="GridOffset"] > div[class^="GridItemStyle"] {
                            width: 100% !important;
                            flex-basis: 100% !important;
                            max-width: 100% !important;
                        }
                    `,
                    (WIDGETS.imageGrid as any).desktopNoWrap
                        ? css`
                              > div[class^="GridOffset"] {
                                  flex-wrap: nowrap;
                              }
                          `
                        : null,
                    (WIDGETS.imageGrid as any).desktopNoWrap
                        ? css`
                              > div[class^="GridOffset"] {
                                  flex-wrap: nowrap;
                              }
                          `
                        : null,
                ])}
        `,
    },
    preTitle: {
        variant: "legend",
        css: css`
            text-align: left;
            margin-bottom: 0;
            display: block;
            padding: 0;
            text-transform: ${(WIDGETS.imageGrid.preTitle as any).textTransform || "none"};
            color: ${(WIDGETS.imageGrid.preTitle as any).color || getColor("text.accent")};
            letter-spacing: ${WIDGETS.imageGrid.preTitle.letterSpacing};
            ${(WIDGETS.imageGrid.preTitle as any).fontFamily ? `font-family: ${(WIDGETS.imageGrid.preTitle as any).fontFamily};` : ""}
            ${(WIDGETS.imageGrid.preTitle as any).fontStyle ? `font-style: ${(WIDGETS.imageGrid.preTitle as any).fontStyle};` : ""}
            ${(WIDGETS.imageGrid.preTitle as any).fontSize ? `font-size: ${(WIDGETS.imageGrid.preTitle as any).fontSize};` : ""}
            ${(WIDGETS.imageGrid.preTitle as any).fontWeight ? `font-weight: ${(WIDGETS.imageGrid.preTitle as any).fontWeight};` : ""}
            ${(WIDGETS.imageGrid.preTitle as any).lineHeight ? `line-height: ${(WIDGETS.imageGrid.preTitle as any).lineHeight};` : ""}
            ${(WIDGETS.imageGrid.preTitle as any).mobileFontSize
                ? ({ theme }: { theme: BaseTheme }) =>
                      breakpointMediaQueries(theme, [
                          css`
                              font-size: ${(WIDGETS.imageGrid.preTitle as any).mobileFontSize};
                          `,
                          css`
                              font-size: ${(WIDGETS.imageGrid.preTitle as any).mobileFontSize};
                          `,
                          css`
                              font-size: ${(WIDGETS.imageGrid.preTitle as any).mobileFontSize};
                          `,
                          null,
                          null,
                      ])
                : ""}
        `,
    },
    mainTitle: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 6px;
            /* text-transform: uppercase; */
        `,
    },
    desc: {
        variant: "p",
        css: css`
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            font-size: ${TYPOGRAPHY.size.small};
            text-align: center;
        `,
    },
    fullWidth: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
        `,
    },
    contentTitleStyles: {
        variant: WIDGETS.imageGrid.contentTitle.variant,
        css: css`
            margin-bottom: ${(WIDGETS.imageGrid.contentTitle as any).marginBottom || "30px"};
            width: 100%;
            white-space: pre-line;
            /* text-transform: capitalize; */
            color: ${getColor("text.main")} !important;
            font-size: ${WIDGETS.imageGrid.contentTitle.fontSize};
            ${(WIDGETS.imageGrid.contentTitle as any).fontWeight ? `font-weight: ${(WIDGETS.imageGrid.contentTitle as any).fontWeight};` : ""}
            ${(WIDGETS.imageGrid.contentTitle as any).lineHeight ? `line-height: ${(WIDGETS.imageGrid.contentTitle as any).lineHeight};` : ""}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: ${(WIDGETS.imageGrid.contentTitle as any).mobileMarginBottom || "20px"};
                        font-size: ${WIDGETS.imageGrid.contentTitle.responsiveFontSizes[0]};
                    `,
                    css`
                        margin-bottom: ${(WIDGETS.imageGrid.contentTitle as any).mobileMarginBottom || "20px"};
                        font-size: ${WIDGETS.imageGrid.contentTitle.responsiveFontSizes[1]};
                    `,
                    css`
                        margin-bottom: ${(WIDGETS.imageGrid.contentTitle as any).mobileMarginBottom || "20px"};
                        font-size: ${WIDGETS.imageGrid.contentTitle.responsiveFontSizes[2]};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    contentDescStyles: {
        variant: "body",
        css: css`
            margin-bottom: ${(WIDGETS.imageGrid as any).contentDesc?.marginBottom || "28px"};
            font-size: ${TYPOGRAPHY.p.size};
            line-height: ${TYPOGRAPHY.p.lineHeight};
            ${(WIDGETS.imageGrid as any).contentDesc?.fontWeight ? `font-weight: ${(WIDGETS.imageGrid as any).contentDesc.fontWeight};` : ""}
            ${(WIDGETS.imageGrid as any).contentDesc?.letterSpacing ? `letter-spacing: ${(WIDGETS.imageGrid as any).contentDesc.letterSpacing};` : ""}
            p {
                margin: 0 !important;
                font-size: ${TYPOGRAPHY.p.size};
                line-height: ${TYPOGRAPHY.p.lineHeight};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: ${(WIDGETS.imageGrid as any).contentDesc?.mobileMarginBottom || "20px"};
                    `,
                    css`
                        margin-bottom: ${(WIDGETS.imageGrid as any).contentDesc?.mobileMarginBottom || "20px"};
                    `,
                    css`
                        margin-bottom: ${(WIDGETS.imageGrid as any).contentDesc?.mobileMarginBottom || "20px"};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    contactInfo: {
        variant: "p",
        css: css`
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            strong {
                margin-right: 5px;
            }
            margin-bottom: 2px;
        `,
    },
    contactInfoContainer: {
        css: css`
            width: 100%;
        `,
    },
    cta: {
        color: COLORS.darkmagenta,
        css: css`
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};

            .email-cta {
                text-decoration: underline;
            }
        `,
    },
    linksContainer: {
        css: css`
            margin-top: ${(WIDGETS.imageGrid as any).linksContainer?.marginTop || "20px"};
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: ${(WIDGETS.imageGrid as any).linksContainer?.mobileMarginTop ||
                        (WIDGETS.imageGrid as any).linksContainer?.marginTop ||
                        "20px"};
                        overflow-x: auto;
                        white-space: nowrap;
                    `,
                    css`
                        margin-top: ${(WIDGETS.imageGrid as any).linksContainer?.mobileMarginTop ||
                        (WIDGETS.imageGrid as any).linksContainer?.marginTop ||
                        "20px"};
                        overflow-x: auto;
                        white-space: nowrap;
                    `,
                    null,
                    null,
                    null,
                ])}

            &.links-inline {
                display: flex;
                align-items: center;
                gap: 20px;

                @media (max-width: ${(WIDGETS.imageGrid as any).linksContainer?.compactBreakpoint || "380px"}) {
                    gap: ${(WIDGETS.imageGrid as any).linksContainer?.compactGap || "8px"};
                    overflow-x: visible;

                    > div a,
                    > div a span,
                    > span {
                        font-size: ${(WIDGETS.imageGrid as any).linksContainer?.compactFontSize || "12px"} !important;
                    }
                }

                > div {
                    margin-bottom: 0;
                }

                > div a {
                    margin-bottom: 0;
                }
            }
        `,
    },
    brandImgStyles: {
        css: css`
            max-width: 200px;
            max-height: 81px;
            margin-bottom: 18px;
        `,
    },
    brandDateStyles: {
        css: css`
            width: 200px;
            height: 40px;
            background: ${getColor("primary")};
            color: white;
            margin-bottom: 18px;
            font-weight: 700;
            font-size: 15px;
            line-height: 22px;
            padding: 8px 15px;
            display: flex;
            justify-content: center;
        `,
    },
};

const styles = imageGridStyles;

const defaultCustomCss = `

.imgGridItem{

}

.mainImgGridItem{

}

.brandImage{
    
}

`;

const CustomLinkWrapper = styled.div<{
    variant?: string;
    textColor?: string;
    children?: React.ReactNode;
}>`
    ${props =>
        props.variant === "link"
            ? css`
                  margin-bottom: ${(WIDGETS.imageGrid as any).linkWrapper?.marginBottom || "20px"};

                  && {
                      letter-spacing: ${TYPOGRAPHY.body.letterSpacing};

                      span {
                          letter-spacing: ${TYPOGRAPHY.body.letterSpacing};
                          display: inline;
                          text-align: left;
                      }
                  }
              `
            : css`
                  a {
                      outline: 0;
                      background-color: ${props.variant === "golden" ? COLORS.golden : COLORS.white};
                      border: 1px solid ${props.variant === "golden" ? COLORS.golden : COLORS.darkmagenta};
                      height: 44px;
                      padding: 10px 34px;
                      min-width: 356px;
                      max-width: 100%;
                      display: block;
                      margin-bottom: 20px;

                      span {
                          display: block;
                          text-align: center;
                          font-size: ${TYPOGRAPHY.size.base};
                          line-height: ${TYPOGRAPHY.lineHeight.xbase};
                          font-weight: ${TYPOGRAPHY.weight.bold};
                      }
                  }
              `}
`;

const emailsRegexp = new RegExp("^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$");
const phoneRegexp = /^[+]?(\d{1,2})?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

const SurImgGridContent: React.FC<Props> = ({ fields }) => {
    const ctaLinks = useGetLinks(fields.links, o => o.fields.link).links;
    const { url } = useGetLink(fields.imageLink);
    const isVideo = fields.mediaType === "video";
    const mobileImageFirst = fields.mobileImagePosition !== "Bottom";

    const bgColor = fields.backgroundColor || "";
    const paddingTB = fields.paddingTopBottom || "";
    const paddingLR = fields.paddingLeftRight || "";
    const mobilePaddingTB = fields.mobilePaddingTopBottom || "";
    const mobilePaddingLR = fields.mobilePaddingLeftRight || "";
    const hasCmsPadding = !!(paddingTB || paddingLR || mobilePaddingTB || mobilePaddingLR);

    const mobileMarginTop = fields.mobileMarginTop || fields.marginTop || "";
    const textAlign = fields.textAlign || "left";
    const isInlineLinks = fields.linksLayout === "inline";

    const customwrapper = {
        css: css`
            ${bgColor ? `background-color: ${bgColor};` : ""}
            ${fields.marginTop ? `margin-top: ${fields.marginTop};` : ""}
            ${({ theme }: { theme: BaseTheme }) =>
                mobileMarginTop
                    ? breakpointMediaQueries(theme, [
                          css`
                              margin-top: ${mobileMarginTop} !important;
                          `,
                          css`
                              margin-top: ${mobileMarginTop} !important;
                          `,
                          null,
                          null,
                          null,
                      ])
                    : ""}
            ${fields.customCSS}
        `,
    };

    const containerCss: InjectableCss = hasCmsPadding
        ? {
              css: css`
                  ${styles.container!.css}
                  > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                      padding: ${paddingTB || "0"} ${paddingLR || "0"} !important;
                  }
                  ${({ theme }: { theme: BaseTheme }) =>
                      breakpointMediaQueries(theme, [
                          css`
                              > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                                  padding: ${mobilePaddingTB || paddingTB || "0"} ${mobilePaddingLR || paddingLR || "0"} !important;
                              }
                          `,
                          css`
                              > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                                  padding: ${mobilePaddingTB || paddingTB || "0"} ${mobilePaddingLR || paddingLR || "0"} !important;
                              }
                          `,
                          css`
                              > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                                  padding: ${mobilePaddingTB || paddingTB || "0"} ${mobilePaddingLR || paddingLR || "0"} !important;
                              }
                          `,
                          null,
                          null,
                      ])}
              `,
          }
        : styles.container!;

    const imgAspectRatio = fields.imageAspectRatio || "";

    const ImageGridStyles: GridItemProps = {
        width: [...WIDGETS.imageGrid.imageWidth],
        css: css`
            max-width: ${WIDGETS.imageGrid.imageMaxWidth} !important;
            padding: 0 !important;
            order: ${fields.imageAligment === "Left" ? 0 : 1};
            img {
                width: 100%;
                height: auto;
                object-fit: ${imgAspectRatio === "auto" ? "contain" : "cover"};
                aspect-ratio: ${imgAspectRatio || WIDGETS.imageGrid.image.aspectRatio};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: none !important;
                        order: ${mobileImageFirst ? 0 : 1} !important;
                        img {
                            aspect-ratio: ${imgAspectRatio || (WIDGETS.imageGrid.image as any).mobileAspectRatio || "1/1"};
                            height: auto;
                        }
                    `,
                    css`
                        max-width: none !important;
                        order: ${mobileImageFirst ? 0 : 1} !important;
                        img {
                            aspect-ratio: ${imgAspectRatio || (WIDGETS.imageGrid.image as any).mobileAspectRatio || "1/1"};
                            height: auto;
                        }
                    `,
                    css`
                        flex-basis: ${WIDGETS.imageGrid.imageFlexBasis};
                        max-width: ${WIDGETS.imageGrid.imageMaxWidth === "none" ? "none" : WIDGETS.imageGrid.imageFlexBasis};
                    `,
                    (WIDGETS.imageGrid as any).desktopImageFlexBasis
                        ? css`
                              flex-basis: ${(WIDGETS.imageGrid as any).desktopImageFlexBasis};
                          `
                        : null,
                    (WIDGETS.imageGrid as any).desktopImageFlexBasis
                        ? css`
                              flex-basis: ${(WIDGETS.imageGrid as any).desktopImageFlexBasis};
                          `
                        : null,
                ])}
        `,
    };

    const ContentGridStyles: GridItemProps = {
        width: [...WIDGETS.imageGrid.contentWidth],
        align: "middle",
        css: css`
            padding: 0 !important;
            order: ${fields.imageAligment === "Left" ? 1 : 0};
            padding-left: ${fields.imageAligment === "Left" ? WIDGETS.imageGrid.padding.left : 0};
            padding-right: ${fields.imageAligment === "Right" ? WIDGETS.imageGrid.padding.right : 0};
            max-width: ${WIDGETS.imageGrid.contentMaxWidth} !important;
            ${textAlign !== "left" ? `text-align: ${textAlign};` : ""}
            ${textAlign === "center" ? "align-items: center;" : ""}
            ${textAlign === "right" ? "align-items: flex-end;" : ""}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: ${(WIDGETS.imageGrid as any).mobileContentPadding || "0 14px"} !important;
                        order: ${mobileImageFirst ? 1 : 0} !important;
                        margin-top: ${mobileImageFirst ? (WIDGETS.imageGrid as any).mobileContentGap || "15px" : "0"} !important;
                    `,
                    css`
                        padding: ${(WIDGETS.imageGrid as any).mobileContentPadding || "0 14px"} !important;
                        order: ${mobileImageFirst ? 1 : 0} !important;
                        margin-top: ${mobileImageFirst ? (WIDGETS.imageGrid as any).mobileContentGap || "15px" : "0"} !important;
                    `,
                    css`
                        padding: ${(WIDGETS.imageGrid as any).mobileContentPadding || "0 14px"} !important;
                        flex-basis: ${WIDGETS.imageGrid.contentFlexBasis};
                        max-width: ${WIDGETS.imageGrid.contentMaxWidth === "none" ? "none" : WIDGETS.imageGrid.contentFlexBasis};
                    `,
                    (WIDGETS.imageGrid as any).desktopContentFlexBasis
                        ? css`
                              flex-basis: ${(WIDGETS.imageGrid as any).desktopContentFlexBasis};
                          `
                        : null,
                    (WIDGETS.imageGrid as any).desktopContentFlexBasis
                        ? css`
                              flex-basis: ${(WIDGETS.imageGrid as any).desktopContentFlexBasis};
                          `
                        : null,
                ])}
        `,
    };

    return (
        <StyledWrapper {...customwrapper}>
            <StyledWrapper {...containerCss} className="max-width-container">
                {fields.mainTitle || fields.infoDesc ? (
                    <StyledWrapper {...styles.infoTitleContainer}>
                        {fields.mainTitle ? <Typography {...styles.mainTitle}>{fields.mainTitle}</Typography> : null}
                        {fields.infoDesc ? <Typography {...styles.desc}>{fields.infoDesc}</Typography> : null}
                    </StyledWrapper>
                ) : null}
                <GridContainer {...styles.gridContainer}>
                    <GridItem {...ImageGridStyles} className="imgGridItem">
                        {isVideo && fields.gridVideo ? (
                            <VideoBanner
                                src={fields.gridVideo}
                                poster={fields.videoPoster}
                                fallbackImage={fields.gridImage}
                                loop={fields.videoLoop ?? true}
                                muted={fields.videoMuted ?? true}
                                autoplay={fields.videoAutoplay ?? true}
                                hideControls={fields.videoHideControls ?? true}
                                playsInline={fields.videoPlaysInline ?? true}
                                aspectRatio={WIDGETS.imageGrid.image.aspectRatio}
                            />
                        ) : (
                            <Img src={fields.gridImage} altText={fields.mainTitle || "MainImage"} className="mainImgGridItem" />
                        )}
                    </GridItem>
                    <GridItem {...ContentGridStyles}>
                        <StyledWrapper
                            {...styles.fullWidth}
                            style={{
                                alignItems: textAlign === "center" ? "center" : textAlign === "right" ? "flex-end" : "flex-start",
                                textAlign: textAlign as any,
                            }}
                        >
                            {fields.brandImage ? (
                                <LazyImage src={fields.brandImage} {...styles.brandImgStyles} altText="BrandImage" className="brandImage" />
                            ) : null}
                            {fields.brandDates ? <StyledWrapper {...styles.brandDateStyles}>{fields.brandDates}</StyledWrapper> : null}
                            {fields.preTitle ? (
                                <StyledWrapper>{fields.preTitle ? <Typography {...styles.preTitle}>{fields.preTitle}</Typography> : null}</StyledWrapper>
                            ) : null}
                            {fields.contentTitle ? (
                                <StyledWrapper>
                                    {url ? (
                                        <Link href={url} target={fields.openInNewWidow ? `_blank` : `_self`} aria-label={fields.contentTitle}>
                                            <Typography {...styles.contentTitleStyles}>{fields.contentTitle}</Typography>
                                        </Link>
                                    ) : (
                                        <Typography {...styles.contentTitleStyles}>{fields.contentTitle}</Typography>
                                    )}
                                </StyledWrapper>
                            ) : null}
                            {fields.contentDesc ? (
                                <Typography
                                    as="div"
                                    {...styles.contentDescStyles}
                                    css={css`
                                        ${isGv ? "width: 95%;" : "width: 100%;"}
                                    `}
                                >
                                    {parse(fields.contentDesc, parserOptions)}
                                </Typography>
                            ) : null}
                            {fields.addContactDetails ? (
                                <StyledWrapper {...styles.contactInfoContainer}>
                                    {fields.hours ? (
                                        <Typography {...styles.contactInfo}>
                                            <strong>Hours:</strong>
                                            {fields.hours}
                                        </Typography>
                                    ) : null}

                                    {fields.email ? (
                                        <Typography {...styles.contactInfo}>
                                            <strong>Email:</strong>
                                            {emailsRegexp.test(fields.email) ? (
                                                <Link {...styles.cta} href={`mailto:${fields.email}`} aria-label={fields.email}>
                                                    <span className="email-cta">{fields.email}</span>
                                                </Link>
                                            ) : (
                                                `Invalid Email`
                                            )}
                                        </Typography>
                                    ) : null}

                                    {fields.phone ? (
                                        <Typography {...styles.contactInfo}>
                                            <strong>Phone:</strong>
                                            {phoneRegexp.test(String(fields.phone)) ? (
                                                <Link {...styles.cta} href={`tel:${fields.phone}`}>
                                                    {fields.phone}
                                                </Link>
                                            ) : (
                                                `Invalid Phone`
                                            )}
                                        </Typography>
                                    ) : null}
                                </StyledWrapper>
                            ) : null}
                            {fields.addLinks && fields.links.length ? (
                                <StyledWrapper {...styles.linksContainer} className={isInlineLinks ? "links-inline" : ""}>
                                    {fields.links.map((item, index) => {
                                        const linksCta = {
                                            css: css`
                                                background-color: ${item.fields.buttonBackgroundColor} !important;
                                                border-color: ${item.fields.buttonBackgroundColor} !important;
                                                color: ${item.fields.buttonTextColor} !important;

                                                span {
                                                    color: ${item.fields.buttonTextColor} !important;
                                                }
                                            `,
                                        };
                                        return (
                                            <React.Fragment key={uniqueId()}>
                                                {isInlineLinks && index > 0 && <span style={{ fontSize: "15px", lineHeight: 1.5 }}>|</span>}
                                                {item.fields.variant === "link" ? (
                                                    <CustomLinkWrapper variant={item.fields.variant} textColor={item.fields.buttonTextColor}>
                                                        <StyledLink
                                                            href={ctaLinks[index].url}
                                                            target={item.fields.openInNewWindow ? `_blank` : `_self`}
                                                            textColor={item.fields.buttonTextColor || (ELEMENTS as any).ctaLink?.color}
                                                            aria-label={item.fields.label}
                                                        >
                                                            {item.fields.label}
                                                        </StyledLink>
                                                    </CustomLinkWrapper>
                                                ) : (
                                                    <CustomLinkWrapper variant={item.fields.variant}>
                                                        <Link
                                                            href={ctaLinks[index].url}
                                                            target={item.fields.openInNewWindow ? `_blank` : `_self`}
                                                            {...linksCta}
                                                            aria-label={item.fields.label}
                                                        >
                                                            {item.fields.label}
                                                        </Link>
                                                    </CustomLinkWrapper>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </StyledWrapper>
                            ) : null}
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const videoTab = {
    displayName: "Video",
    sortOrder: 3,
};

const isVideoMode = (item: any) => item?.fields?.[fields.mediaType] === "video";

const SurImgGridContentWidget: WidgetModule = {
    component: SurImgGridContent,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.mainTitle,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.infoDesc,
                displayName: "Sub Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.mediaType,
                displayName: "Media Type",
                editorTemplate: "DropDownField",
                defaultValue: "image",
                fieldType: "General",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Video", value: "video" },
                ],
                sortOrder: -1,
            },
            {
                name: fields.gridImage,
                fieldType: "General",
                displayName: "Grid Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: contentTab,
                sortOrder: 0,
            },
            {
                name: fields.gridVideo,
                fieldType: "Translatable",
                displayName: "Video URL",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: videoTab,
                isVisible: isVideoMode,
                sortOrder: 0,
            },
            {
                name: fields.videoPoster,
                fieldType: "Translatable",
                displayName: "Poster Image (shown while video loads)",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: videoTab,
                isVisible: isVideoMode,
                sortOrder: 1,
            },
            {
                name: fields.videoAutoplay,
                fieldType: "General",
                displayName: "Autoplay",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: videoTab,
                isVisible: isVideoMode,
                sortOrder: 2,
            },
            {
                name: fields.videoLoop,
                fieldType: "General",
                displayName: "Loop",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: videoTab,
                isVisible: isVideoMode,
                sortOrder: 3,
            },
            {
                name: fields.videoMuted,
                fieldType: "General",
                displayName: "Muted (required for autoplay)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: videoTab,
                isVisible: isVideoMode,
                sortOrder: 4,
            },
            {
                name: fields.videoHideControls,
                fieldType: "General",
                displayName: "Hide Controls",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: videoTab,
                isVisible: isVideoMode,
                sortOrder: 5,
            },
            {
                name: fields.videoPlaysInline,
                fieldType: "General",
                displayName: "Plays Inline (no fullscreen on iOS)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: videoTab,
                isVisible: isVideoMode,
                sortOrder: 6,
            },
            {
                name: fields.preTitle,
                displayName: "Pre-Title/Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 0.5,
            },
            {
                name: fields.contentTitle,
                displayName: "Title",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 1,
            },
            {
                name: fields.imageLink,
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.openInNewWidow,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },

            {
                name: fields.contentDesc,
                displayName: "Title",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 2,
            },
            {
                name: fields.brandImage,
                displayName: "Brand Name",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 7,
            },
            {
                name: fields.brandDates,
                displayName: "Brand dates",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 8,
            },

            {
                name: fields.hours,
                displayName: "Hours",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields.addContactDetails,
                tab: contentTab,
                sortOrder: 3,
            },
            {
                name: fields.email,
                displayName: "Email",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "example@email.com",
                isVisible: (item: HasFields) => item.fields.addContactDetails,
                tab: contentTab,
                sortOrder: 4,
            },
            {
                name: fields.phone,
                displayName: "Contact Number",
                editorTemplate: "TextField",
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields.addContactDetails,
                defaultValue: "",
                tab: contentTab,
                sortOrder: 5,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.label;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 6,
                isVisible: (item: HasFields) => item.fields.addLinks,
                tab: contentTab,
                fieldDefinitions: [
                    {
                        name: "label",
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "link",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                    {
                        name: "buttonBackgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Button Background Color",
                        defaultValue: "",
                    },
                    {
                        name: "buttonTextColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Button Text Color",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { value: "normal", displayName: "Normal" },
                            { value: "golden", displayName: "Golden" },
                            { value: "link", displayName: "Link" },
                        ],
                        defaultValue: "normal",
                    },
                ],
            },

            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS color value for the container background.",
                placeholder: "transparent",
                tab: settingsTab,
                sortOrder: -5,
            },
            {
                name: fields.paddingTopBottom,
                displayName: "Padding Top/Bottom (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS value for top and bottom padding on desktop.",
                placeholder: "e.g. 20px",
                tab: settingsTab,
                sortOrder: -4,
            },
            {
                name: fields.paddingLeftRight,
                displayName: "Padding Left/Right (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS value for left and right padding on desktop.",
                placeholder: "e.g. 40px",
                tab: settingsTab,
                sortOrder: -3,
            },
            {
                name: fields.mobilePaddingTopBottom,
                displayName: "Padding Top/Bottom (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS value for top and bottom padding on mobile. Falls back to desktop value if empty.",
                placeholder: "e.g. 20px",
                tab: settingsTab,
                sortOrder: -2,
            },
            {
                name: fields.mobilePaddingLeftRight,
                displayName: "Padding Left/Right (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS value for left and right padding on mobile. Falls back to desktop value if empty.",
                placeholder: "e.g. 16px",
                tab: settingsTab,
                sortOrder: -1,
            },
            {
                name: fields.marginTop,
                displayName: "Margin Top",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS value for top margin on desktop. Default: 0px",
                placeholder: "e.g. 40px",
                tab: settingsTab,
                sortOrder: -0.5,
            },
            {
                name: fields.mobileMarginTop,
                displayName: "Margin Top (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS value for top margin on mobile. Falls back to desktop value if empty.",
                placeholder: "e.g. 20px",
                tab: settingsTab,
                sortOrder: -0.45,
            },
            {
                name: fields.imageAspectRatio,
                displayName: "Image Aspect Ratio",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS aspect-ratio for the image. Use 'auto' for natural proportions. Empty = token default.",
                placeholder: "e.g. auto, 1/1, 16/9",
                tab: settingsTab,
                sortOrder: -0.4,
            },
            {
                name: fields.imageAligment,
                fieldType: "General",
                displayName: "Image Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { value: "Left", displayName: "Left" },
                    { value: "Right", displayName: "Right" },
                ],
                defaultValue: "Left",
                tab: settingsTab,
                sortOrder: 0,
            },
            {
                name: fields.mobileImagePosition,
                fieldType: "General",
                displayName: "Mobile Image Position",
                editorTemplate: "DropDownField",
                options: [
                    { value: "Top", displayName: "Top" },
                    { value: "Bottom", displayName: "Bottom" },
                ],
                defaultValue: "Top",
                tab: settingsTab,
                sortOrder: 1,
            },
            {
                name: fields.addContactDetails,
                displayName: "Add Contact Details",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: settingsTab,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.addLinks,
                displayName: "Add Links",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: settingsTab,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.linksLayout,
                displayName: "Links Layout",
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                tooltip: "Vertical stacks links. Inline displays them side by side with a pipe separator (dual CTA style).",
                options: [
                    { displayName: "Vertical", value: "vertical" },
                    { displayName: "Inline (pipe separator)", value: "inline" },
                ],
                isVisible: (item: HasFields) => item.fields.addLinks,
                tab: settingsTab,
                sortOrder: 2.5,
            },
            {
                name: fields.textAlign,
                displayName: "Text Alignment",
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                tooltip: "Horizontal alignment of the text content block.",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                tab: settingsTab,
                sortOrder: 2.6,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};
export default SurImgGridContentWidget;
